// ---------------------------------------------------------------------------
// Renderer Helpers — shared utilities used by multiple block renderers.
//
// Extracted from VizDslRenderer.js.  These depend on Konva and cannot live
// in server/shared/ (which must be Konva-free).
// ---------------------------------------------------------------------------

import Konva from "konva";
import CustomText from "../CustomText";
import { formatValue } from "../shared/formatting.js";
import { evaluateFormula } from "../shared/formula-evaluator.js";
import { blockRegistry } from "../shared/block-registry/init.js";

// ── Debug overlay ───────────────────────────────────────────────────────

export function addDebugOverlay(group, block, options) {
  if (!options.debug) return;
  const { width = 100, height = 50 } = options;
  const s = options.fontScale || 1;

  const regColor = blockRegistry.getDebugColor(block.type);
  const colors = {
    text_block: regColor === "rgba(128,128,128,0.03)" ? "rgba(100,149,237,0.04)" : regColor,
    chart: "rgba(60,179,113,0.04)",
    tile_row: "rgba(255,165,0,0.04)",
    conditional: "rgba(186,85,211,0.04)",
    bullet_list: "rgba(220,20,60,0.04)",
    flow: "rgba(128,128,128,0.03)"
  };
  const fill = colors[block.type] || regColor;
  group.add(new Konva.Rect({
    width, height: group.height() || height,
    fill, stroke: "rgba(0,0,0,0.10)", strokeWidth: 1, dash: [4, 4],
    listening: false
  }));

  const actualH = group.height() || 0;
  const fs = Math.round(s * 100);
  const sizingInfo = block.type === "flow"
    ? `  ${block.sizing || "content"}/${block.expand || "none"}`
    : "";
  const label = `${block.type} #${block.id}${sizingInfo}  fs:${fs}%  alloc:${Math.round(width)}×${Math.round(height)}  h:${Math.round(actualH)}`;
  group.add(new CustomText({
    x: 2, y: 0, width: width - 4,
    text: label, fontSize: Math.round(9 * s), fill: "rgba(0,0,0,0.85)",
    fontFamily: "monospace", lineHeight: 1.2,
    listening: false
  }));
}

// ── Scale-group resolution ──────────────────────────────────────────────

export function measureTextNaturalWidth(spec) {
  const t = new Konva.Text({
    text: spec.text, fontSize: spec.fontSize,
    fontStyle: spec.fontStyle, fontFamily: spec.fontFamily
  });
  const w = t.width();
  t.destroy();
  return w;
}

function pushRecord(map, key, record) {
  if (!map.has(key)) map.set(key, []);
  map.get(key).push(record);
}

function normalizeTile(tile, dataContext, blockNullFallback) {
  const valueSpec = tile.value || {};
  const labelSpec = tile.label || {};

  let raw;
  if (valueSpec.formula) raw = evaluateFormula(valueSpec.formula, dataContext);
  else if (valueSpec.extract) raw = dataContext[valueSpec.extract];

  const isNull = raw == null || (typeof raw === "number" && !Number.isFinite(raw));
  let displayValue;
  if (isNull) {
    displayValue = (valueSpec.null_fallback || blockNullFallback || {}).display || "N/A";
  } else if (!valueSpec.format && typeof raw === "number" && Math.abs(raw) >= 100000) {
    // No format declared and the value is a large raw number (LLM returned a
    // number where a formatted string was expected) — compact it rather than
    // printing "5000000000". No currency symbol: the unit is unknown here.
    displayValue = formatValue(raw, "num_compact");
    if (valueSpec.suffix) displayValue += valueSpec.suffix;
  } else {
    displayValue = formatValue(raw, valueSpec.format, {
      currency: dataContext?._currency,
      valuesUnit: dataContext?._values_unit
    });
    if (valueSpec.suffix) displayValue += valueSpec.suffix;
  }
  const valueColor = isNull
    ? ((valueSpec.null_fallback || blockNullFallback || {}).color || "#999")
    : "#1a1a1a";

  let labelText = labelSpec.text || "";
  if (!labelText && labelSpec.extract) {
    const v = dataContext[labelSpec.extract];
    if (typeof v === "string" && v.length > 0) labelText = v;
  }

  return {
    displayValue, valueColor, labelText, isNull,
    valueScaleGroup: valueSpec.scaleGroup,
    labelScaleGroup: labelSpec.scaleGroup
  };
}

export function resolveTileScaleGroups({ tiles, dataContext, blockNullFallback, fontScale, innerW, resetGroups }) {
  const s = fontScale;
  const groupRecords = new Map();

  for (const tile of tiles) {
    const norm = normalizeTile(tile, dataContext, blockNullFallback);
    const valueFontMax = Math.round(26 * s);
    const labelFontMax = Math.round(11 * s);

    if (norm.valueScaleGroup) {
      const naturalW = measureTextNaturalWidth({
        text: String(norm.displayValue), fontSize: valueFontMax, fontStyle: "bold"
      });
      pushRecord(groupRecords, norm.valueScaleGroup, {
        naturalWidth: naturalW, allocatedWidth: innerW, requestedSize: valueFontMax
      });
    }
    if (norm.labelScaleGroup && norm.labelText) {
      const naturalW = measureTextNaturalWidth({
        text: String(norm.labelText), fontSize: labelFontMax
      });
      pushRecord(groupRecords, norm.labelScaleGroup, {
        naturalWidth: naturalW, allocatedWidth: innerW, requestedSize: labelFontMax
      });
    }
  }

  const scaleMap = new Map();
  for (const [groupName, records] of groupRecords) {
    const cfg = (resetGroups && resetGroups[groupName]) || {};
    const fit = cfg.fit || "vertical";
    const floor = cfg.min_scale ?? 0.4;
    let scale = 1;
    if (fit === "width") {
      for (const r of records) {
        if (r.naturalWidth > 0) {
          scale = Math.min(scale, r.allocatedWidth / r.naturalWidth);
        }
      }
    }
    scaleMap.set(groupName, Math.max(floor, Math.min(1, scale)));
  }
  return scaleMap;
}

// ── Evidence tagging ─────────────────────────────────────────────────────

export function tagEvidence(node, provKey, options) {
  const p = options && options.provenance && options.provenance[provKey];
  if (!node || !p || !p.evidence) return;
  node.setAttr("_evidence", p.evidence);
  node.setAttr("_evidenceVerified", p.verified !== false);
  node.setAttr("_evidenceProvKey", provKey);
  if (p.sourceIds && p.sourceIds.length > 0) node.setAttr("_evidenceSourceIds", p.sourceIds);
  node.listening(true);
  const w = node.width();
  const h = node.height();
  if (w && h) {
    node.hitFunc(function (ctx, shape) {
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.closePath();
      ctx.fillStrokeShape(shape);
    });
  }
}

// Re-export normalizeTile for tile_row renderer
export { normalizeTile };
